import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../utils/i18n";
import GymLogo from "../components/GymLogo";
// import NotificationBell from "../components/NotificationBell";
import api from "../services/api";
import { useCurrency } from "../utils/currency";
import { formatISTDateTime, formatISTDate, formatISTTime, formatDuration } from "../utils/dateUtils";
import {
  HandRaisedIcon,
  ShieldCheckIcon,
  UsersIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowRightIcon,
  BoltIcon,
  UserIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  BanknotesIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useTranslation(user?.gym_id);
  const { formatCurrency, setCurrencyCode } = useCurrency(user?.gym_id);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [gymStatus, setGymStatus] = useState(null);
  const [livePresence, setLivePresence] = useState(null);
  const [livePresenceLoading, setLivePresenceLoading] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    fetchGymStatus();
    fetchLivePresence(true);

    const interval = setInterval(() => {
      fetchLivePresence(false);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const fetchLivePresence = async (isInitial = false) => {
    try {
      if (isInitial) setLivePresenceLoading(true);
      const response = await api.get('/api/attendance/live-presence');
      setLivePresence(response.data);
    } catch (err) {
      console.error('Failed to fetch live presence:', err);
    } finally {
      if (isInitial) setLivePresenceLoading(false);
    }
  };

  const handleMarkExit = async (attendanceId) => {
    try {
      await api.put(`/api/attendance/${attendanceId}/mark-exit`);
      fetchLivePresence();
      fetchAnalytics(); // Refresh analytics to update counts
    } catch (err) {
      console.error('Failed to mark exit:', err);
      alert(err.response?.data?.error || 'Failed to mark exit');
    }
  };

  const fetchGymStatus = async () => {
    try {
      const response = await api.get('/api/gym/status');
      setGymStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch gym status:', err);
    }
  };

  const handleGymStatusToggle = async () => {
    if (!gymStatus) return;
    
    const newStatus = gymStatus.operational_status === 'Open' ? 'Closed' : 'Open';
    
    try {
      const response = await api.put('/api/gym/status', {
        operational_status: newStatus
      });
      // Update gym status while preserving show_gym_status
      setGymStatus({
        ...gymStatus,
        operational_status: response.data.operational_status
      });
    } catch (err) {
      console.error('Failed to update gym status:', err);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get("/api/analytics/dashboard");
      setAnalytics(response.data);
      if (response.data?.currency) {
        setCurrencyCode(response.data.currency);
      }
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
      setError(
        err.response?.data?.error || "Failed to load dashboard analytics",
      );
    } finally {
      setLoading(false);
    }
  };

  const getGrowthColor = (growth) => {
    if (growth > 0) return "text-green-600";
    if (growth < 0) return "text-red-600";
    return "text-gray-600";
  };

  const getGrowthIcon = (growth) => {
    if (growth > 0) return <ArrowTrendingUpIcon className="w-4 h-4 inline" />;
    if (growth < 0) return <ArrowTrendingDownIcon className="w-4 h-4 inline" />;
    return <ArrowRightIcon className="w-4 h-4 inline" />;
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-600">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8 max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-red-50 border border-red-200 p-6 rounded-2xl text-center shadow-sm">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-bold text-red-800 mb-2">Failed to Load Dashboard</h3>
          <p className="text-sm text-red-600 mb-6">{error}</p>
          <button
            onClick={() => {
              setError("");
              setLoading(true);
              fetchAnalytics();
              fetchGymStatus();
              fetchLivePresence(true);
            }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold transition-all shadow-md shadow-red-600/10 cursor-pointer group"
          >
            <ArrowPathIcon className="w-4 h-4 transition-transform duration-500 group-hover:rotate-180" /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-4">
          <GymLogo className="w-12 h-12" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
              {t('dashboard.welcomeBack')}, {user?.name || "Gym Owner"}{" "}
              <HandRaisedIcon className="w-6 h-6 text-orange-500" />
            </h1>
            <p className="text-sm text-gray-600">
              Here's what's happening at your gym today - Gym ID: {user?.gym_id}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Gym Status Badge */}
          {gymStatus?.show_gym_status && (
            <button
              onClick={handleGymStatusToggle}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white hover:opacity-90 transition-opacity cursor-pointer ${
                gymStatus.operational_status === 'Open' 
                  ? 'bg-green-500' 
                  : 'bg-red-500'
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${
                gymStatus.operational_status === 'Open' ? 'bg-white' : 'bg-white'
              }`}></div>
              {gymStatus.operational_status === 'Open' ? '🟢 OPEN' : '🔴 CLOSED'}
            </button>
          )}
          {/* <NotificationBell /> */}
          {/* <div className="flex items-center gap-1.5 text-xs bg-blue-50 text-blue-700 px-3 py-2 rounded-xl border border-blue-200 font-bold self-start md:self-auto">
            <ClockIcon className="w-4 h-4 text-blue-600" /> IST (Asia/Kolkata)
          </div> */}
          {/* <div className="flex items-center gap-2 text-xs bg-orange-50 text-orange-700 px-3.5 py-2 rounded-xl border border-orange-200 font-bold self-start md:self-auto">
            <ShieldCheckIcon className="w-4 h-4" /> Multi-Tenant Active
          </div> */}
        </div>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Members */}
        <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs uppercase font-extrabold tracking-wider text-gray-600">
              {t('dashboard.totalMembers')}
            </span>
            <UsersIcon className="w-6 h-6 text-orange-500" />
          </div>
          <p className="text-3xl font-black text-gray-900">
            {analytics?.members?.total || 0}
          </p>
          <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
            <span
              className={`font-semibold ${getGrowthColor(analytics?.members?.growth_percentage || 0)}`}
            >
              {getGrowthIcon(analytics?.members?.growth_percentage || 0)}{" "}
              {Math.abs(analytics?.members?.growth_percentage || 0)}%
            </span>{" "}
 {t('dashboard.growth')}
          </div>
        </div>

        {/* Today's Check-ins */}
        <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs uppercase font-extrabold tracking-wider text-gray-600">
              {t('attendance.todayAttendance')}
            </span>
            <CalendarIcon className="w-6 h-6 text-orange-500" />
          </div>
          <p className="text-3xl font-black text-gray-900">
            {analytics?.attendance?.todays_checkins || 0}
          </p>
          <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
            <span className="text-orange-600 font-semibold">
              {analytics?.attendance?.currently_inside || 0}
            </span>{" "}
 {t('attendance.presentMembers')}
          </div>
        </div>

        {/* Monthly Revenue */}
        <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs uppercase font-extrabold tracking-wider text-gray-600">
              {t('dashboard.monthlyRevenue')}
            </span>
            <CurrencyDollarIcon className="w-6 h-6 text-orange-500" />
          </div>
          <p className="text-3xl font-black text-gray-900">
            {formatCurrency(analytics?.payments?.current_month_revenue || 0)}
          </p>
          <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
            <span
              className={`font-semibold ${getGrowthColor(analytics?.payments?.revenue_growth || 0)}`}
            >
              {getGrowthIcon(analytics?.payments?.revenue_growth || 0)}{" "}
              {Math.abs(analytics?.payments?.revenue_growth || 0)}%
            </span>{" "}
 {t('dashboard.lastMonth')}
          </div>
        </div>

        {/* Membership Plans */}
        <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs uppercase font-extrabold tracking-wider text-gray-600">
              {t('plans.title')}
            </span>
            <CreditCardIcon className="w-6 h-6 text-orange-500" />
          </div>
          <p className="text-3xl font-black text-gray-900">
            {analytics?.membership_plans?.active || 0}
          </p>
          <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
            <span className="text-orange-600 font-semibold">
              {analytics?.membership_plans?.total || 0}
            </span>{" "}
 {t('dashboard.total')}
          </div>
        </div>
      </div>

      {/* Live Presence Tracking - 3 Distinct States */}
      {(() => {
        const activeList = livePresence?.members_active || (livePresence?.currently_inside?.filter(m => !m.is_timed_out) || []);
        const timedOutList = livePresence?.members_timed_out || (livePresence?.currently_inside?.filter(m => m.is_timed_out) || []);
        const exitedList = livePresence?.members_left_today || [];

        return (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* 1. Members Checked In (Active - Green) */}
            <div className="bg-white border border-green-200 p-6 rounded-2xl shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-md font-bold text-emerald-800 flex items-center gap-2">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                  1. Members Checked In (Active)
                </h2>
                <div className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-black">
                  {activeList.length}
                </div>
              </div>
              
              {livePresenceLoading && !livePresence ? (
                <div className="text-center py-8 text-gray-500 text-xs">Loading active members...</div>
              ) : activeList.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {activeList.map((member) => (
                    <div key={member.attendance_id} className="p-3.5 rounded-xl border border-emerald-200 bg-emerald-50/60 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-extrabold text-gray-900 text-sm">{member.member_name}</p>
                          <p className="text-[11px] text-emerald-700 font-semibold">{member.membership_plan || 'Standard Gym Plan'}</p>
                        </div>
                        <button
                          onClick={() => handleMarkExit(member.attendance_id)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-1 shrink-0"
                        >
                          <XMarkIcon className="w-4 h-4" /> Check Out / Exit
                        </button>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-gray-600 border-t border-emerald-100 pt-1.5">
                        <span>Entry (IST): <strong>{formatISTTime(member.check_in_time)}</strong></span>
                        <span>Duration: <strong>{member.duration_inside_mins || 0} mins</strong></span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircleIcon className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                  <p className="text-xs text-gray-600 font-medium">No active members inside</p>
                </div>
              )}
            </div>

            {/* 2. Members Time Out (Red Alert) */}
            <div className="bg-white border border-red-200 p-6 rounded-2xl shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-md font-bold text-red-700 flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  2. Members Time Out (Red Alert)
                </h2>
                <div className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-black">
                  {timedOutList.length}
                </div>
              </div>
              
              {livePresenceLoading && !livePresence ? (
                <div className="text-center py-8 text-gray-500 text-xs">Loading timed out members...</div>
              ) : timedOutList.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {timedOutList.map((member) => (
                    <div key={member.attendance_id} className="p-3.5 rounded-xl border border-red-200 bg-red-50/60 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-1.5">
                            <p className="font-extrabold text-gray-900 text-sm">{member.member_name}</p>
                            <span className="bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">
                              Time Out
                            </span>
                          </div>
                          <p className="text-[11px] text-red-700 font-semibold mt-0.5">
                            Overtime: {member.duration_inside_mins || 120} mins inside
                          </p>
                        </div>
                        <button
                          onClick={() => handleMarkExit(member.attendance_id)}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-1 shrink-0"
                        >
                          <XMarkIcon className="w-4 h-4" /> Check Out / Exit
                        </button>
                      </div>
                      {member.is_plan_expired && (
                        <div className="text-[10px] font-bold text-red-800 bg-red-100 px-2 py-0.5 rounded border border-red-200">
                          ⚠️ Membership Plan Expired
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ExclamationTriangleIcon className="w-10 h-10 text-red-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-600 font-medium">No members timed out</p>
                </div>
              )}
            </div>

            {/* 3. Members Exited Today (Checked Out) */}
            <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-md font-bold text-gray-800 flex items-center gap-2">
                  <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                  3. Members Exited (Left Gym)
                </h2>
                <div className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-black">
                  {livePresence?.members_left_count || 0}
                </div>
              </div>
              
              {livePresenceLoading ? (
                <div className="text-center py-8 text-gray-500 text-xs">Loading...</div>
              ) : livePresence?.members_left_today?.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {livePresence.members_left_today.map((member) => (
                    <div key={member.attendance_id} className="p-3.5 rounded-xl border border-gray-200 bg-gray-50 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{member.member_name}</p>
                          <p className="text-[11px] text-gray-500">{member.membership_plan || 'Standard Plan'}</p>
                        </div>
                        <span className="bg-gray-200 text-gray-700 text-[10px] font-bold px-2 py-0.5 rounded">
                          Exited
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-gray-600 border-t border-gray-200 pt-1.5">
                        <span>Entry: <strong>{formatISTTime(member.check_in_time)}</strong></span>
                        <span>Exit: <strong>{formatISTTime(member.check_out_time)}</strong></span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <UserIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-600 font-medium">No members exited today</p>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Charts and Analytics Row */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Weekly Attendance Chart */}
        <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ChartBarIcon className="w-5 h-5 text-orange-500" /> {t('attendance.weeklyAttendance')}
          </h2>
          <div className="space-y-3">
            {analytics?.attendance?.weekly_trend?.map((day, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-16 text-xs text-gray-600 font-medium">
                  {formatISTDate(day.date)}
                </div>
                <div className="flex-1 bg-gray-200 rounded-full h-2 relative">
                  <div
                    className="bg-orange-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.max((day.count / Math.max(...(analytics?.attendance?.weekly_trend?.map((d) => d.count) || [1]))) * 100, 5)}%`,
                    }}
                  ></div>
                </div>
                <div className="w-8 text-sm font-semibold text-gray-900">
                  {day.count}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly Revenue Chart */}
        <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ArrowTrendingUpIcon className="w-5 h-5 text-orange-500" /> {t('dashboard.monthlyTrends')}
          </h2>
          <div className="space-y-3">
            {analytics?.payments?.monthly_trend?.map((month, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-16 text-xs text-gray-600 font-medium">
                  {month.month}
                </div>
                <div className="flex-1 bg-gray-200 rounded-full h-2 relative">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.max((month.revenue / Math.max(...(analytics?.payments?.monthly_trend?.map((m) => m.revenue) || [1]))) * 100, 5)}%`,
                    }}
                  ></div>
                </div>
                <div className="w-20 text-xs font-semibold text-gray-900 text-right">
                  {formatCurrency(month.revenue)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions & Pending Items */}
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Quick Actions */}
        <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BoltIcon className="w-5 h-5 text-orange-500" /> {t('dashboard.quickStats')}
          </h2>
          <div className="space-y-3">
            <Link
              to="/members/add"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-orange-50 transition-colors group"
            >
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                <UserIcon className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{t('members.addMember')}</p>
                <p className="text-xs text-gray-500">{t('members.memberList')}</p>
              </div>
            </Link>
            <Link
              to="/attendance/checkin"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-orange-50 transition-colors group"
            >
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                <CalendarIcon className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {t('attendance.checkIn')}
                </p>
                <p className="text-xs text-gray-500">{t('attendance.attendanceHistory')}</p>
              </div>
            </Link>
            <Link
              to="/payments/add"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-orange-50 transition-colors group"
            >
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                <CurrencyDollarIcon className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {t('payments.addPayment')}
                </p>
                <p className="text-xs text-gray-500">{t('payments.paymentHistory')}</p>
              </div>
            </Link>
            <Link
              to="/membership-plans/add"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-orange-50 transition-colors group"
            >
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                <CreditCardIcon className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{t('plans.addPlan')}</p>
                <p className="text-xs text-gray-500">{t('plans.planDetails')}</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Pending Payments */}
        <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <ClockIcon className="w-5 h-5" /> {t('dashboard.pendingPayments')}
            </h2>
            <Link
              to="/payments?status=Pending"
              className="text-xs text-orange-600 hover:text-orange-700 font-medium"
            >
              {t('dashboard.viewAll')}
            </Link>
          </div>
          {analytics?.payments?.pending_count > 0 ? (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-yellow-800">
                      {analytics.payments.pending_count} {t('dashboard.pendingPayments')}
                    </p>
                    <p className="text-xs text-yellow-600">
                      Total: {formatCurrency(analytics.payments.pending_amount)}
                    </p>
                  </div>
                  <BanknotesIcon className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
              <Link
                to="/payments?status=Pending"
                className="block w-full bg-yellow-600 hover:bg-yellow-700 text-white text-center py-2 px-4 rounded-lg text-sm font-medium transition-colors"
              >
                {t('dashboard.viewAll')}
              </Link>
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircleIcon className="w-10 h-10 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                {t('payments.noPaymentsFound')}
              </p>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ArrowPathIcon className="w-5 h-5" /> {t('dashboard.recentMembers')}
          </h2>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {analytics?.members?.recent_members?.map((member) => (
              <div
                key={`member-${member.id}`}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50"
              >
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-xs font-semibold text-blue-700">
                    {member.first_name?.charAt(0) || "?"}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {member.first_name} {member.last_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    New member • {formatISTDateTime(member.created_at)}
                  </p>
                </div>
              </div>
            ))}
            {analytics?.payments?.recent_payments
              ?.slice(0, 3)
              .map((payment) => (
                <div
                  key={`payment-${payment.id}`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50"
                >
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <CurrencyDollarIcon className="w-4 h-4 text-green-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      Payment: {formatCurrency(payment.payment_amount)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {payment.member_name} •{" "}
                      {formatISTDateTime(payment.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            {analytics?.attendance?.recent_attendance
              ?.slice(0, 2)
              .map((record) => (
                <div
                  key={`attendance-${record.id}`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50"
                >
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <CalendarIcon className="w-4 h-4 text-orange-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {record.status}: {record.member_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatISTDateTime(record.check_in_time)}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* System Status */}
      {/* <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <ShieldCheckIcon className="w-5 h-5" /> System Status & Multi-Tenant
          Isolation
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Data Isolation</h3>
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              All data in this workspace is securely isolated by your Gym ID.
              Members, payments, attendance, and plans are filtered to show only
              your gym's data.
            </p>
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
              <p className="text-xs font-mono text-orange-600 font-semibold">
                WHERE gym_id = {user?.gym_id || "NULL"}
              </p>
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">System Health</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Database Connection
                </span>
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  <span className="text-xs text-green-600 font-medium">
                    Connected
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">API Services</span>
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  <span className="text-xs text-green-600 font-medium">
                    Operational
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Data Sync</span>
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  <span className="text-xs text-green-600 font-medium">
                    Real-time
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
}
